import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    inject,
} from '@angular/core';
import { AuthenticatedMediaService } from 'app/modules/shared/services/authenticated-media.service';
import { Subscription } from 'rxjs';

/** 1×1 transparent GIF — keeps layout without the browser “broken image” icon while the blob loads. */
const TRANSPARENT_PIXEL =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Use on `<img>` instead of `[src]` when the URL may be an authenticated API
 * path (`uploads/get-file`, company logos on the API host, etc.).
 *
 * While an API blob is in flight the host gets `.auth-src--loading` (token
 * skeleton shimmer in `_sai-components.scss`, no broken-icon flash). After
 * decode it clears via `.auth-src--ready`. If the fetch fails, or the blob
 * fetches fine but cannot be decoded as an image (e.g. a deleted/expired
 * file, or a non-image error body returned with a 200), the host falls back
 * to `.auth-src--error` (static muted tile) instead of leaving the browser's
 * native broken-image icon + alt text on screen.
 *
 * ```html
 * <img [authSrc]="item.featured_image" alt="" />
 * ```
 *
 * Note: Angular’s `NgOptimizedImage` is for public CDN URLs with static
 * placeholders — it cannot attach `Authorization`. Keep using this directive
 * for API media.
 */
@Directive({
    selector: 'img[authSrc]',
    standalone: true,
    host: {
        'class': 'auth-src',
        '[class.auth-src--loading]': 'loading',
        '[class.auth-src--ready]': 'ready',
        '[class.auth-src--error]': 'errored',
    },
})
export class AuthSrcDirective implements OnChanges, OnDestroy {
    private readonly _el = inject(ElementRef<HTMLImageElement>);
    private readonly _media = inject(AuthenticatedMediaService);
    private readonly _cdr = inject(ChangeDetectorRef);
    private _loadSub: Subscription | null = null;
    private _decodeCleanup: (() => void) | null = null;

    /** True while an uncached API blob fetch (or image decode) is in progress. */
    loading = false;
    /** True once the current `src` has decoded successfully. */
    ready = false;
    /** True when the fetch failed, or the fetched blob could not be decoded as an image. */
    errored = false;

    @Input({ required: true }) authSrc: string | null | undefined;

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['authSrc']) {
            return;
        }
        this._apply(this.authSrc);
    }

    ngOnDestroy(): void {
        this._teardown();
    }

    private _apply(url: string | null | undefined): void {
        this._teardown();

        const img = this._el.nativeElement;
        const trimmed = (url ?? '').trim();

        if (!trimmed) {
            this._setState(false, false, false);
            img.src = TRANSPARENT_PIXEL;
            return;
        }

        const waitingOnNetwork =
            this._media.isApiHosted(trimmed) && !this._media.hasCached(trimmed);

        if (waitingOnNetwork) {
            this._setState(true, false, false);
            // Keep a prior good frame if present; otherwise use a transparent pixel (no broken icon).
            if (!img.getAttribute('src') || img.src === TRANSPARENT_PIXEL) {
                img.src = TRANSPARENT_PIXEL;
            }
        }

        this._loadSub = this._media.resolve(trimmed).subscribe((resolved: string): void => {
            if (!resolved) {
                img.src = TRANSPARENT_PIXEL;
                this._setState(false, false, true);
                return;
            }

            if (img.src === resolved && img.complete && img.naturalWidth > 0) {
                this._setState(false, true, false);
                return;
            }

            this._setState(true, false, false);
            this._awaitDecode(
                img,
                (): void => {
                    this._setState(false, true, false);
                },
                (): void => {
                    // Blob fetched but couldn't be decoded as an image (deleted/expired file,
                    // non-image error body, etc.) — fall back to the transparent pixel instead
                    // of leaving the browser's broken-image icon + alt text on screen.
                    img.src = TRANSPARENT_PIXEL;
                    this._setState(false, false, true);
                }
            );
            img.src = resolved;
        });
    }

    private _awaitDecode(img: HTMLImageElement, onLoad: () => void, onError: () => void): void {
        this._decodeCleanup?.();
        const handleLoad = (): void => {
            cleanup();
            onLoad();
        };
        const handleError = (): void => {
            cleanup();
            onError();
        };
        const cleanup = (): void => {
            img.removeEventListener('load', handleLoad);
            img.removeEventListener('error', handleError);
            this._decodeCleanup = null;
        };
        img.addEventListener('load', handleLoad);
        img.addEventListener('error', handleError);
        this._decodeCleanup = cleanup;
    }

    private _setState(loading: boolean, ready: boolean, errored: boolean): void {
        this.loading = loading;
        this.ready = ready;
        this.errored = errored;
        this._cdr.markForCheck();
    }

    private _teardown(): void {
        this._loadSub?.unsubscribe();
        this._loadSub = null;
        this._decodeCleanup?.();
        this._decodeCleanup = null;
    }
}
